/**
 * The case view's member search and the Find Member wizard: the words and the small pure rules, in ONE
 * place, so the drawer's Search members box and the wizard say the same thing.
 *
 * PART F (William, 16 Oct 2026 about 11:10 PM: "Search did not work still"): a search that found nobody says
 * WHICH of two things happened. The member list could not be read (the server's emptyReason
 * 'source-unreachable', or the request failed), or the list was read and nobody matched. The first is never
 * shown as the second.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
import type { MemberSearchResp } from '@lib/care-model';

export const MEMBER_SEARCH_WORDING = {
  unread: (plan: string): string => `The ${plan} member list could not be read, so nobody could be searched. Try again in a minute.`,
  none: (q: string): string => `No member matched "${q}". Try the last name, the date of birth (5/20/1954), or both.`,
} as const;

/** True when the server said the roster could not be read (never "nobody matched"). */
export function memberSearchUnread(r: Pick<MemberSearchResp, 'emptyReason'> | null | undefined): boolean {
  return r?.emptyReason === 'source-unreachable';
}

/* ------------------------------ PART A: the Find Member wizard ------------------------------ */

/** The decisions the wizard offers, in its order (the server's MATCH_DECISIONS, pinned in lockstep). */
export const MATCH_DECISIONS = ['opt-out-campaign', 'opt-out-all', 'stop', 'none'] as const;
export type MatchDecision = (typeof MATCH_DECISIONS)[number];

export const FIND_MEMBER_WORDING = {
  button: 'Find Member',
  head: 'Find Member',
  /** Names the client chosen in the header, whose member list is searched (William, 17 Oct 2026). */
  lede: (plan: string): string => `Search ${plan}'s member list by name and date of birth (or phone), tick the one right member, then Match Member. The match and the decision go to the Approval Queue; nothing changes until the case is approved.`,
  name: 'Name',
  dob: 'Date of birth',
  phone: 'Phone',
  scopeThis: (roster: string): string => `This campaign (${roster})`,
  scopeAll: (plan: string): string => `Every campaign of ${plan}`,
  search: 'Search',
  searching: 'Searching...',
  decision: 'Decision on approval',
  reason: 'Reason (what the member asked for)',
  match: 'Match Member',
  matching: 'Queuing...',
  cancel: 'Close',
  none: 'No member matched these details. Try the last name alone, or the date of birth alone.',
  onlyOne: 'Tick one member only.',
  pending: (name: string, id: string, decision: string): string => `Match pending review: ${name} (#${id}), ${decision}. Nothing changes until the case is approved.`,
  /** Console fault 5 (27 Oct 2026): why Match Member is grey, said under it instead of nothing. */
  reasonNeeded: 'Type a reason to match with this decision.',
  /** Console fault 1 (27 Oct 2026): with opt-out everywhere on, a match with a decision goes to the Approval Queue at once. */
  sendsNow: 'Match Member sends this case to the Approval Queue now.',
  campaignOff: 'Opt out of this campaign is not switched on for this console, so it cannot be applied at approval.',
  decisionWords: {
    'opt-out-campaign': 'Opt out of this campaign',
    'opt-out-all': 'Opt out of everything',
    stop: 'Stop (Do Not Contact)',
    none: 'No decision (match only)',
  } as Record<MatchDecision, string>,
} as const;

/** What the case asked for, as the wizard's starting decision: an ask-out is "opt out of this campaign". */
export function deriveDecision(ev: {
  suggestedAction?: string | null | undefined;
  claireRequestType?: string | null | undefined;
  requests?: ReadonlyArray<{ type?: string }> | null | undefined;
  actions?: ReadonlyArray<{ type?: string; data?: Record<string, unknown> | undefined }> | null | undefined;
}, campaignOffered: boolean): MatchDecision {
  const acts = ev.actions ?? [];
  if (acts.some((a) => a.type === 'do-not-contact')) return 'stop';
  if (acts.some((a) => a.type === 'opt-out' && String(a.data?.['scope'] ?? '') === 'all')) return 'opt-out-all';
  const asksOut = ev.suggestedAction === 'opt-out'
    || String(ev.claireRequestType ?? '').toLowerCase() === 'opt_out'
    || (ev.requests ?? []).some((r) => r.type === 'opt-out')
    || acts.some((a) => a.type === 'opt-out' || a.type === 'decline');
  if (!asksOut) return 'none';
  return campaignOffered ? 'opt-out-campaign' : 'none';
}

/** 1954-05-20 or 5/20/1954 -> 5/20/1954 (the search reads either); '' stays ''. */
export function dobForSearch(v: string): string {
  const t = v.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : t;
}

/** The searches the wizard runs: name AND date of birth as one query, and the phone as another. */
export function findMemberQueries(f: { name: string; dob: string; phone: string }): string[] {
  const out: string[] = [];
  const nameDob = [f.name.trim(), dobForSearch(f.dob)].filter(Boolean).join(' ');
  if (nameDob.length >= 2) out.push(nameDob);
  const digits = f.phone.replace(/\D+/g, '');
  if (digits.length >= 10) out.push(digits.slice(-10));
  return out;
}

/** The last four digits of a phone, or ''. */
export function lastFour(phone: string | null | undefined): string {
  const d = String(phone ?? '').replace(/\D+/g, '');
  return d.length >= 4 ? d.slice(-4) : '';
}

/* ------------------------------ PART L: Send to Approval Queue ------------------------------ */

export const APPROVAL_QUEUE_WORDING = {
  button: 'Send to Approval Queue',
  sending: 'Sending...',
  help: 'Sends the case with its dispositions, the Find Member match, notes and attachments to the Approval Queue. Nothing takes effect until it is approved.',
  failed: (status: number, why: string): string => `Not sent to the Approval Queue (HTTP ${status}${why ? `: ${why}` : ''}). The case stays open.`,
} as const;

/**
 * A row the operator sent to the Approval Queue from this page is hidden: at once (before the server's list
 * catches up), and while the list says it is in review. Once the list shows it new or in progress again (it was
 * returned or recalled), it shows again.
 */
export function sentAwayHides(sent: ReadonlyMap<string, number>, row: { eventId: string; status: string }): boolean {
  if (!sent.has(row.eventId)) return false;
  return row.status !== 'new' && row.status !== 'in_progress' ? true : (Date.now() - (sent.get(row.eventId) ?? 0)) < 15000;
}
